export const STREAM_RENDER_STEP_MS = 34;
export const STREAM_RENDER_MIN_CHARS = 3;
export const STREAM_RENDER_MAX_CHARS = 22;

export const firstPresentThreadTitle = (...titles) => {
  const title = titles.find((candidate) => String(candidate || "").trim());
  return String(title || "").trim() || "Chat";
};

export const mergeThreadMessageIntoThreads = (prev, payload) => {
  const safePrev = Array.isArray(prev) ? prev : [];
  const threadUuid = payload?.threadUuid;
  if (!threadUuid) return safePrev;

  const msg = payload?.message;
  const existing = safePrev.find((row) => row?.threadUuid === threadUuid);

  if (!msg) {
    if (!existing) return safePrev;

    return safePrev.map((row) =>
      row?.threadUuid === threadUuid
        ? {
            ...row,
            title: firstPresentThreadTitle(payload?.threadTitle, row?.title),
            updatedAt: payload?.updatedAt || row?.updatedAt,
            isAdminTargeted: Boolean(
              payload?.isAdminTargeted ?? row?.isAdminTargeted,
            ),
          }
        : row,
    );
  }

  const nextThread = {
    ...(existing || {}),
    threadUuid,
    title: firstPresentThreadTitle(payload?.threadTitle, existing?.title),
    updatedAt: payload?.updatedAt || existing?.updatedAt,
    isAdminTargeted: Boolean(payload?.isAdminTargeted ?? existing?.isAdminTargeted),
    pairedUserUsername: existing?.pairedUserUsername,
  };
  return [
    nextThread,
    ...safePrev.filter((row) => row?.threadUuid !== threadUuid),
  ];
};

export const buildBotMessage = (message) => ({
  __stableKey: `stk-${Math.random().toString(36).slice(2, 10)}`,
  createdAt: "2023-09-29T22:28:02Z",
  message,
  advancedFilteringMessage: "",
  sender: "bot",
  tickerDescription: [],
  answeredTickerIds: [],
  currentPrice: "",
  closePrice: "",
  priceChange1d: "",
  pricePctChange1d: "",
  pricePctChangeAh: "",
  pricePctChange24hr: "",
  assetType: "",
  ticker: "",
  currentPriceUpdatedAt: "",
  logoUrls: [],
  tickerPrices: null,
  sportsCardData: null,
  agenticChartState: null,
  earningsInfo: null,
  revenueInfo: null,
  // Flips to true via finishStreamIfIdle when WS text streaming ends. Chart
  // subtree stays hidden until then; historical messages from the DB load
  // without this field (undefined) so they render normally.
  textStreamCompleted: false,
});

export const mergeSportsCardDataIntoLatestBot = (prev, sportsCardData) => {
  if (
    !sportsCardData ||
    typeof sportsCardData !== "object" ||
    Object.keys(sportsCardData).length === 0
  ) {
    return Array.isArray(prev) ? prev : [];
  }

  const safePrev = Array.isArray(prev) ? prev : [];
  const last = safePrev.length ? safePrev[safePrev.length - 1] : null;
  if (!last || last.sender !== "bot") {
    return [
      ...safePrev,
      {
        ...buildBotMessage(""),
        sportsCardData,
      },
    ];
  }

  const next = safePrev.slice();
  next[next.length - 1] = {
    ...last,
    sportsCardData,
  };
  return next;
};

export const appendBotMessageText = (setMessageThreads, text) => {
  if (!text) return;
  setMessageThreads((prev) => {
    if (prev.length && prev[prev.length - 1].sender === "bot") {
      const next = prev.slice();
      next[next.length - 1] = {
        ...next[next.length - 1],
        message: `${next[next.length - 1].message || ""}${text}`,
      };
      return next;
    }

    return [...prev, buildBotMessage(text)];
  });
};

export const replaceBotMessageText = (setMessageThreads, text) => {
  setMessageThreads((prev) => {
    if (prev.length && prev[prev.length - 1].sender === "bot") {
      const next = prev.slice();
      next[next.length - 1] = {
        ...next[next.length - 1],
        message: text,
      };
      return next;
    }

    return [...prev, buildBotMessage(text)];
  });
};

export const getRenderChunkSize = (pendingLength) => {
  if (pendingLength >= 220) return STREAM_RENDER_MAX_CHARS;
  if (pendingLength >= 140) return 18;
  if (pendingLength >= 80) return 12;
  if (pendingLength >= 36) return 8;
  return STREAM_RENDER_MIN_CHARS;
};

export const takeRenderChunk = (pendingText) => {
  if (!pendingText) return ["", ""];

  const target = Math.min(
    pendingText.length,
    getRenderChunkSize(pendingText.length),
  );

  if (pendingText.length <= target) {
    return [pendingText, ""];
  }

  const preferred = pendingText
    .slice(0, target + 12)
    .match(/^([\s\S]{1,34}?[\s,.!?;:)\]}]+)(?=[^\s]|$)/);

  if (preferred?.[1]?.length && preferred[1].length >= target) {
    const chunk = preferred[1];
    return [chunk, pendingText.slice(chunk.length)];
  }

  const chunk = pendingText.slice(0, target);
  return [chunk, pendingText.slice(target)];
};
